/**
 * ltfevidence.ts — draw the LTF EVIDENCE record. Compute nothing, and claim nothing.
 *
 * Card `t_bf04f7ec`. Every number here arrived from `/api/overlays/{a}/{tf}/ltf_evidence`, which
 * is `ict.ltf_evidence.observe`. There is no threshold, no window, no ATR multiple and no rule in
 * this file, and there must never be — `overlays.ts`' standing contract: geometry in, pixels out.
 *
 * *** THIS LAYER IS NOT A SIGNAL AND MUST NEVER BECOME ONE. *** It draws no entry, no stop, no
 * target, no order, no size, no rank and no score. It draws facts a reviewer compares against the
 * candles, and the bar each fact became knowable on. `test_ltf_evidence_overlay.py` asserts the
 * absence over the served JSON, and `ltfevidence.test.ts` asserts it over what this file returns.
 *
 * WHAT IS DRAWN, AND WHY EACH SHAPE
 *
 *   markers      one per event, at the bar it HAPPENED on, with `text: ''`. The empty text is
 *                MEASURED, not styled: lightweight-charts' `setMarkers` has no collision handling
 *                whatsoever, and labelling break markers produced 189 labels of which 173
 *                overlapped (91.5%) with fragments like `CHoCHoCH` (`overlays.breakMarkers`' own
 *                note). The word belongs on a collision-tested plate, which is the segment's.
 *   segments     *** THE KNOWABILITY GAP, MADE VISIBLE. *** One dashed segment per event whose
 *                `knowable_time` is LATER than its `time`, running between the two. That gap IS
 *                the look-ahead risk this record exists to expose — the same argument
 *                `chartapp/api/overlays.py` makes for serving `confirm_time` beside `time` — and
 *                an event knowable on its own bar gets NO segment rather than a zero-length one.
 *   summary      counts by kind, MSS as a subset of the breaks, plus the refusals and the repaint
 *                disclosure. A row that shows only what was found cannot be told from one that
 *                looked and found nothing.
 *
 * DISCLOSURE IS COMPUTED ON THE SAME SIDE OF THE FILTER AS THE DRAWING (`.hermes.md` §9c). Both
 * builders return what they handed the chart, and `ltfEvidenceSummary` counts from THAT rather
 * than from the payload, so a row can never claim a shape that is not on screen.
 */
import type { SeriesMarker, Time, UTCTimestamp } from 'lightweight-charts';
import type { BreakSegment } from './breakprimitive';
import { BOS_COLOR, CHOCH_COLOR } from './overlays';

/** The six event kinds. Mirrors `ict.ltf_evidence.EVENT_KINDS`, and the mirror is asserted:
 *  `ltfevidence.test.ts` compares this list against the server's `counts` keys, because a
 *  hand-maintained copy of a backend vocabulary is a second source of truth. */
export const LTF_KINDS = ['sweep', 'bos', 'choch', 'displacement', 'fvg', 'invalidation'] as const;
export type LtfKind = (typeof LTF_KINDS)[number];

export type LtfEvent = {
  kind: LtfKind;
  /** The bar the thing HAPPENED on. */
  idx: number;
  time: UTCTimestamp | null;
  /** The bar a STREAMING observer first saw it. Never earlier than `idx`, and measurably later
   *  than the detector's own `confirm_idx` on real data. */
  knowable_idx: number;
  knowable_time: UTCTimestamp | null;
  /** 'bullish' | 'bearish' for a break; the swept book SIDE ('buy' | 'sell') for a sweep; null
   *  where the fact has no direction. NOT an instruction — see the module header. */
  direction: string | null;
  price: number | null;
  /** Which `ict/` function produced it, verbatim. */
  source: string;
  /** A BOS is never an MSS, and neither is a bootstrap CHoCH. Served, never re-derived here: a
   *  client deriving it from `kind` alone would get the second case wrong. */
  is_mss: boolean;
  facts: Record<string, unknown>;
};

export type LtfEvidencePayload = {
  component: 'ltf_evidence';
  source: string;
  params: {
    decision_idx: number;
    decision_time: number;
    rule_version: string;
    /**
     * THE SERIES BOUND the server computed over. `series_bars` is how many bars the detectors saw;
     * `series_truncated` says whether the feed held MORE, which is the difference between "this is
     * all the history there is" and "we cut it"; `series_offset` is what the server added back to
     * every index to make it absolute into the full series. This file never re-derives the offset --
     * it keys on `time`, per `overlays.ts`' standing rule.
     */
    series_bars: number;
    series_from_time: number | null;
    series_truncated: boolean;
    series_offset: number;
  };
  counts: Record<string, number>;
  counts_all?: Record<string, number>;
  /** WHY things are absent, by name. A mapping, never a row list. */
  refusals: Record<string, number>;
  events: LtfEvent[];
};

/** Colour per kind. ONE table, so marker, segment and legend cannot drift — `breakColor`'s rule.
 *
 *  The break colours are IMPORTED from the breaks overlay rather than re-picked, because a BOS
 *  drawn teal by one layer and olive by another is two claims about one event. */
export const LTF_COLOR: Record<LtfKind, string> = {
  sweep: '#c9a227',
  bos: BOS_COLOR,
  choch: CHOCH_COLOR,
  displacement: '#4a7fb5',
  fvg: '#7f8c8d',
  invalidation: '#8e44ad',
};

/** Marker shape per kind. Direction decides up/down where the event HAS a direction. */
function shapeFor(e: LtfEvent): 'arrowUp' | 'arrowDown' | 'circle' | 'square' {
  if (e.kind === 'bos' || e.kind === 'choch') {
    return e.direction === 'bullish' ? 'arrowUp' : 'arrowDown';
  }
  if (e.kind === 'sweep') return 'circle';
  return 'square';
}

/**
 * One marker per event, at the bar it happened on. Returns what it handed the series.
 *
 * A ROW WITH NO `time` IS SKIPPED AND THAT IS WHY THE RETURN VALUE IS COUNTED. The server sends
 * `null` when an index falls outside the served window, and `timeToCoordinate(null)` draws
 * nothing — so counting the PAYLOAD would print a number the chart cannot show, which is the
 * `drawn N of M` defect `.hermes.md` §9c names. `ltfEvidenceSummary` reads this list.
 */
export function ltfMarkers(p: LtfEvidencePayload): SeriesMarker<Time>[] {
  const out: SeriesMarker<Time>[] = [];
  for (const e of p.events) {
    if (e.time === null) continue;
    const up = e.direction === 'bullish' || e.direction === 'buy';
    out.push({
      time: e.time,
      position: up ? 'belowBar' : 'aboveBar',
      shape: shapeFor(e),
      color: LTF_COLOR[e.kind],
      // An MSS is the one event here that carries reversal information, so it is the one drawn
      // large. A BOS and a bootstrap CHoCH are the same size as everything else, which is the
      // visual form of "neither is a shift".
      size: e.is_mss ? 1 : 0.8,
      // EMPTY. See the module header: uncollided text is confetti.
      text: '',
    } as SeriesMarker<Time>);
  }
  return out.sort((a, b) => (a.time as number) - (b.time as number));
}

/**
 * The KNOWABILITY GAP as bounded dashed segments — one per event that was not knowable on its
 * own bar.
 *
 * `fromTime` is when it happened, `toTime` when a streaming observer first had it. An event with
 * `knowable_time === time` produces NO segment: a zero-length dash is not a fact about anything,
 * and drawing one would put a mark on every event and make the layer unreadable while telling the
 * reader nothing.
 *
 * The label is the KIND, drawn on `BreakPrimitive`'s collision-tested plate — the one place in
 * this app a word may be drawn on a chart.
 */
export function ltfKnowabilitySegments(p: LtfEvidencePayload): BreakSegment[] {
  const out: BreakSegment[] = [];
  for (const e of p.events) {
    if (e.time === null || e.knowable_time === null) continue;
    if (e.knowable_time <= e.time) continue;
    if (e.price === null || !Number.isFinite(e.price)) continue;
    out.push({
      price: e.price,
      fromTime: e.time,
      toTime: e.knowable_time,
      color: LTF_COLOR[e.kind],
      label: e.is_mss ? 'MSS' : e.kind,
      // An MSS outranks the rest visually for the reason `breakSegments` emphasises a CHoCH: it
      // is the event that carries information.
      emphasis: e.is_mss,
    });
  }
  return out;
}

/**
 * The sidebar rows. COUNTED FROM WHAT WAS DRAWN, plus the payload's own disclosures.
 *
 * `drawn` is the marker list this run handed the series, not `p.counts.total`: the two differ
 * whenever a row's bar sits outside the served window, and a count that cannot differ from the
 * picture is the false-clean signal `.hermes.md` §9c forbids.
 *
 * REFUSALS AND `repainted` ARE ALWAYS PRINTED, including when they are zero. "0 repainted" is a
 * measurement; an absent row is indistinguishable from a layer that never looked.
 */
export function ltfEvidenceSummary(
  p: LtfEvidencePayload,
  drawing?: { markers: SeriesMarker<Time>[]; segments: BreakSegment[] },
): [string, string][] {
  const markers = drawing ? drawing.markers : ltfMarkers(p);
  const segments = drawing ? drawing.segments : ltfKnowabilitySegments(p);
  const rows: [string, string][] = [
    ['source', p.source],
    // A LEADING BANNER, because this is the one thing a reader must not mistake. The layer's whole
    // purpose is evidence, and a sidebar that opens with counts invites reading them as a verdict.
    ['what this is', 'facts for review — NOT an entry signal'],
    ['as of bar', String(p.params.decision_idx)],
    ['rule', p.params.rule_version],
    // THE SERIES BOUND, ON SCREEN. The server computes over the last `series_bars` bars, so a break
    // whose structure began earlier is absent BY CONSTRUCTION -- and an absent row is
    // indistinguishable from a quiet market unless the window is stated. `series_truncated` is the
    // difference between "this is all the history there is" and "we cut it", which is why the row
    // says which one happened rather than just printing a number.
    ['series', p.params.series_truncated
      ? `last ${p.params.series_bars} bars (history cut — earlier structure is NOT in this record)`
      : `${p.params.series_bars} bars (the whole series)`],
    ['drawn', `${markers.length} of ${p.counts.total}`],
  ];
  for (const k of LTF_KINDS) {
    const n = p.counts[k];
    if (n === undefined) continue;
    rows.push([k, String(n)]);
  }
  // MSS is a SUBSET of the two break kinds, stated as such: "3" beside "bos 6 / choch 4" would
  // read as a seventh kind.
  const breaks = (p.counts.bos ?? 0) + (p.counts.choch ?? 0);
  rows.push(['mss (of breaks)', `${p.counts.mss ?? 0} of ${breaks}`]);
  rows.push(['knowability gaps drawn', String(segments.length)]);
  rows.push(['repainted', String(p.counts.repainted ?? 0)]);
  for (const [name, n] of Object.entries(p.refusals)) {
    rows.push([`refused: ${name}`, String(n)]);
  }
  return rows;
}

/**
 * ONE CALL that returns every shape AND the numbers describing them, so they cannot drift.
 *
 * `setupDrawing`'s shape, and for its reason: the count a sidebar prints and the geometry on
 * screen are a pair, and computing them at two call sites is what produced "34 zone(s) off
 * screen" over 29 painted zones.
 */
export function ltfEvidenceDrawing(p: LtfEvidencePayload): {
  markers: SeriesMarker<Time>[];
  segments: BreakSegment[];
  rows: [string, string][];
} {
  const markers = ltfMarkers(p);
  const segments = ltfKnowabilitySegments(p);
  return { markers, segments, rows: ltfEvidenceSummary(p, { markers, segments }) };
}
